/**
 * Satzspiegel und Raster eines Frames — die Linien, an denen Inhalte einrasten.
 *
 * Ein Frame ist die Bühne einer Präsentationsseite. Damit über alle Seiten
 * hinweg derselbe Rand steht und Bilder nicht jedes Mal neu „ungefähr" gesetzt
 * werden, bekommt jeder Frame einen Innenrand und ein Raster. Beide sind
 * standardmäßig **automatisch**: Sie leiten sich aus der Größe des Frames ab und
 * wachsen mit ihm mit. Ein fest eingetragenes Raster wäre nach dem ersten
 * Skalieren falsch — und ein falsches Raster ist schlimmer als keines.
 */
import { FRAME_LAYOUT_AUTO } from './board.js';
/** Innenrand als Anteil der kürzeren Frameseite. */
const AUTO_PADDING_RATIO = 0.04;
/** Mehr Spalten als das ergibt Linien, die dichter stehen als die Fangweite. */
const MAX_TRACKS = 12;
/** Wählbare Werte in der Frame-Leiste; `FRAME_LAYOUT_AUTO` steht davor. */
export const TRACK_CHOICES = [0, 2, 3, 4, 6, 8, 12];
export const PADDING_CHOICES = [0, 0.02, 0.04, 0.06, 0.08];
const clampTracks = (value) => Math.max(0, Math.min(MAX_TRACKS, Math.round(value)));
/**
 * Löst „automatisch" gegen die tatsächliche Größe auf.
 *
 * Die Spaltenzahl folgt dem Seitenverhältnis — eine breite Bühne verträgt ein
 * feines Zwölftelraster, ein Hochformat nicht —, die Zeilenzahl folgt daraus, so
 * dass die Zellen annähernd quadratisch bleiben. Erklärt in einem Satz: das
 * Raster soll aussehen wie eines, das jemand von Hand für diesen Frame gelegt
 * hätte.
 */
export function resolveFrameLayout(frame) {
    const short = Math.min(frame.w, frame.h);
    // `padding` ist ein Anteil der kürzeren Seite; hier wird daraus eine Länge.
    const paddingShare = frame.padding === undefined || frame.padding === FRAME_LAYOUT_AUTO
        ? AUTO_PADDING_RATIO
        : Math.max(0, Math.min(0.5, frame.padding));
    const padding = short * paddingShare;
    const inner = innerRectOf(frame, padding);
    const ratio = inner.h > 0 ? inner.w / inner.h : 1;
    const autoColumns = ratio >= 1.2 ? 12 : ratio <= 0.8 ? 4 : 6;
    const columns = frame.columns === undefined || frame.columns === FRAME_LAYOUT_AUTO
        ? autoColumns
        : clampTracks(frame.columns);
    const rows = frame.rows === undefined || frame.rows === FRAME_LAYOUT_AUTO
        ? // Zellen möglichst quadratisch: die Zeilenzahl ergibt sich aus der
            // Spaltenbreite, nicht aus einer zweiten Faustregel.
            Math.max(1, clampTracks(columns / (ratio || 1)))
        : clampTracks(frame.rows);
    return { padding, columns, rows };
}
/** Der Satzspiegel als Rechteck; bei zu kleinem Frame gleich dem Frame selbst. */
export function innerRectOf(frame, padding) {
    const x = frame.x ?? 0;
    const y = frame.y ?? 0;
    // Ein Innenrand, der breiter ist als der halbe Frame, hätte keine Fläche mehr
    // übrig — dann gilt der Frame selbst.
    if (padding <= 0 || padding * 2 >= Math.min(frame.w, frame.h)) {
        return { x, y, w: frame.w, h: frame.h };
    }
    return { x: x + padding, y: y + padding, w: frame.w - padding * 2, h: frame.h - padding * 2 };
}
/**
 * Alle Linien eines Frames, an denen eingerastet wird: Außenkanten, Mitte,
 * Satzspiegel und die Rasterlinien darin.
 */
export function frameSnapLines(frame) {
    const layout = resolveFrameLayout(frame);
    const inner = innerRectOf(frame, layout.padding);
    const x = [frame.x, frame.x + frame.w / 2, frame.x + frame.w];
    const y = [frame.y, frame.y + frame.h / 2, frame.y + frame.h];
    if (inner.w !== frame.w) {
        x.push(inner.x, inner.x + inner.w);
        y.push(inner.y, inner.y + inner.h);
    }
    for (let i = 1; i < layout.columns; i++)
        x.push(inner.x + (inner.w * i) / layout.columns);
    for (let i = 1; i < layout.rows; i++)
        y.push(inner.y + (inner.h * i) / layout.rows);
    return { x, y };
}
//# sourceMappingURL=frameLayout.js.map